import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { useLingui } from '@lingui/react/macro'

import {
  AdminAPI,
  getFragment,
  GraphQL,
  SharedAPI,
  utils
} from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import { GoodieFile, useUpdateFiles } from '@/features/files'
import { showToast, ToastType } from '@/features/toast'
import { formToGraphql, FormTranslations } from '@/features/translations'

export type FormData = {
  type: GraphQL.GoodieType
  name: string
  title: string
  description: string
  files: GoodieFile[]
  integrationDoorId?: number | null
  default: {
    duration: number | null
    price: number
    location: string
    openingHours: GraphQL.RecurrenceFragmentFragment[]
    button: GraphQL.DefaultButtonType
    buttonFile?: GoodieFile | null
    bookWithSuggestions: boolean | null
    buttonLink: string | null
    requestFields: GraphQL.RequestField[] | null
  }
  event: {
    duration: number
    location: string
    price: number
    recurrences: GraphQL.RecurrenceFragmentFragment[]
    seats: number | null
    button: GraphQL.EventButtonType
    hasLimitedSeats: boolean
    isInstantApproval: boolean
    canJoinDuringEvent: boolean
  }
  message: {
    toBeReplaced: boolean
  }
  translations: FormTranslations
}

export const TRANSLATABLE_FIELDS: string[] = [
  'title',
  'description',
  'default.requestFields.[].label',
  'default.requestFields.[].description',
  'default.requestFields.[].options.[]'
]

interface Options {
  id?: number
  onDone: (goodie?: GraphQL.GoodieFragmentFragment, deleted?: boolean) => void
}

export default function useGoodieForm({ id, onDone }: Options) {
  const { t } = useLingui()

  const [updateFiles, { progress }] = useUpdateFiles()

  const [createGoodie] = useMutation(AdminAPI.goodie.CREATE, {
    refetchQueries: [AdminAPI.goodie.QUERY]
  })

  const [updateGoodie] = useMutation(AdminAPI.goodie.UPDATE, {
    update(cache, { data }) {
      if (!data) return

      // Write the raw mutation result to the GET query cache
      if (id) {
        cache.writeQuery({
          query: AdminAPI.goodie.GET,
          variables: {
            id,
            withGallery: true,
            withTranslations: true
          },
          data: {
            getGoodie: data.updateGoodie.goodie
          }
        })
      }
    }
  })

  const [deleteGoodie] = useMutation(AdminAPI.goodie.DELETE, {
    update(cache, { data }) {
      if (!data) return

      const goodie = getFragment(
        SharedAPI.goodie.GOODIE_FRAGMENT,
        data.deleteGoodie
      )

      utils.paginationHelpers.remove(cache, goodie.id, 'Goodie', 'queryGoodies')
    }
  })

  const { errors, handlePromiseCatch } = Form.useErrorHelpers({})

  const uploadFiles = useCallback(
    (files: GoodieFile[], buttonFile?: GoodieFile | null) => {
      const allFiles = buttonFile ? [buttonFile, ...files] : files

      return updateFiles(allFiles).then(files => {
        const ids = files.map(({ id }) => id)

        return {
          fileIds: buttonFile ? ids.splice(1) : ids,
          buttonFileId: buttonFile ? ids[0] : null
        }
      })
    },
    [updateFiles]
  )

  const create = useCallback(
    async ({ files, default: defaultProps, ...data }: FormData) => {
      let buttonFile = null

      if (defaultProps) {
        buttonFile = defaultProps.buttonFile
        delete defaultProps.buttonFile
      }

      return uploadFiles(files, buttonFile)
        .then(({ fileIds: gallery, buttonFileId }) => {
          const variables = {
            input: {
              ...data,
              default:
                data.type === GraphQL.GoodieType.Default
                  ? {
                      ...defaultProps,
                      requestFields: defaultProps.requestFields?.map(field => ({
                        type: field.type,
                        label: field.label,
                        description: field.description,
                        options: field.options
                      })),
                      buttonFileId
                    }
                  : undefined,
              gallery,
              translations: formToGraphql(data.translations)
            }
          } as GraphQL.CreateGoodieMutationVariables

          return createGoodie({
            variables,
            refetchQueries: [AdminAPI.goodie.QUERY]
          })
        })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          showToast({
            type: ToastType.SUCCESS,
            id: 'success-goodie-create',
            message: t({
              comment: 'Success toaster message when creating a goodie worked',
              message: 'Successfully created the goodie'
            })
          })

          const goodie = getFragment(
            SharedAPI.goodie.GOODIE_FRAGMENT,
            data?.createGoodie.goodie
          )

          onDone(goodie)
        })
        .catch(error =>
          handlePromiseCatch(error, {
            unknownString: t({
              comment:
                'Generic error message that we show when something unexpected happens.',
              message:
                'Something went wrong, please try again, if this keeps happening please write our support.'
            })
          })
        )
    },
    [createGoodie, uploadFiles, t, handlePromiseCatch, onDone]
  )

  const update = useCallback(
    // Just need to filter out the type, since we don't send it in the update
    async ({
      files,

      type,
      default: defaultProps,
      ...data
    }: FormData) => {
      if (!id) return

      let buttonFile = null

      if (defaultProps) {
        buttonFile = defaultProps.buttonFile
        delete defaultProps.buttonFile
      }

      return uploadFiles(files, buttonFile)
        .then(({ fileIds: gallery, buttonFileId }) => {
          const variables = {
            input: {
              ...data,
              default:
                type === GraphQL.GoodieType.Default
                  ? {
                      ...defaultProps,
                      requestFields: defaultProps.requestFields?.map(field => ({
                        type: field.type,
                        label: field.label,
                        description: field.description,
                        options: field.options
                      })),
                      buttonFileId
                    }
                  : undefined,
              id,
              gallery,
              translations: formToGraphql(data.translations)
            },
            withGallery: true,
            withTranslations: true
          } as GraphQL.UpdateGoodieMutationVariables

          if (buttonFileId && variables.input.default) {
            variables.input.default.buttonFileId = buttonFileId
          }

          return updateGoodie({ variables })
        })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          const goodie = getFragment(
            SharedAPI.goodie.GOODIE_FRAGMENT,
            data?.updateGoodie.goodie
          )

          const goodieName = goodie.name

          showToast({
            type: ToastType.SUCCESS,
            id: 'success-goodie-update',
            message: t({
              comment:
                'Success toaster message when a goodie was successfuly updated.',
              message: `Successfully updated Goodie "${goodieName}".`
            })
          })

          const notChangedOccs = data.updateGoodie.metadata?.notChanged ?? []

          // TODO: Show Dialog with the Event Occurrences that couldn't update
          if (notChangedOccs.length > 0) {
            showToast({
              id: `event-occs-not-updated-${goodie.id}`,
              message: t({
                comment: `Info message for the user to let them know that some event occurrences weren't updated since they already had bookings`,
                message: `Some Event Occurrences couldn't update since they aready had bookings, please resolve these manually in the events tab.`
              }),
              type: ToastType.INFO
            })
          }

          onDone(goodie)
        })
        .catch(error =>
          handlePromiseCatch(error, {
            unknownString: t({
              comment:
                'Generic error message that we show when something unexpected happens.',
              message:
                'Something went wrong, please try again, if this keeps happening please write our support.'
            })
          })
        )
    },
    [id, onDone, updateGoodie, uploadFiles, t, handlePromiseCatch]
  )

  const remove = useCallback(() => {
    if (!id) return

    deleteGoodie({ variables: { id } })
    onDone(undefined, true)
  }, [id, deleteGoodie, onDone])

  return {
    errors,
    create,
    update,
    remove,
    progress
  }
}
